import { AppSelect } from '../ui/AppSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Download,
  FileText,
  Loader2,
  Music,
  Pause,
  Play,
  RefreshCw,
  Search,
  Sparkles,
  Volume2,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { fetchAudioResources, generateMusicAudio, planScriptMusicCues } from '../../services/audioService';
import { normalizeGlobalProvider } from '../../constants/globalAiProviders';

const DEFAULT_MUSIC_MODEL = 'suno-v5.5';
const DEFAULT_MUSIC_LANGUAGE = 'zh';

const normalizeAudio = (item, fallback = {}) => {
  if (!item) return null;
  const result = item.result || item.data || item;
  const audioUrl = result.audioUrl || result.audio_url || result.url || result.resultUrl || result.result_url || fallback.url;
  if (!audioUrl) return null;
  let metadata = result.metadata || fallback.metadata || null;
  if (typeof metadata === 'string') {
    try {
      metadata = JSON.parse(metadata);
    } catch {
      metadata = null;
    }
  }
  const originalMetadata = metadata?.originalMetadata;
  let parsedOriginalMetadata = originalMetadata;
  if (typeof parsedOriginalMetadata === 'string') {
    try {
      parsedOriginalMetadata = JSON.parse(parsedOriginalMetadata);
    } catch {
      parsedOriginalMetadata = null;
    }
  }
  const source = result.source || fallback.source || metadata?.source || parsedOriginalMetadata?.source || '';
  const sourceLabel = source === 'mini_storyboard_music'
    ? '小故事板音乐'
    : source === 'script_music_audio'
      ? '剧本配乐'
      : source === 'music_audio_agent'
        ? '音乐音频'
        : '';
  return {
    id: result.id || result.taskId || result.providerTaskId || fallback.id || audioUrl,
    audioUrl,
    thumbnailUrl: result.thumbnailUrl || result.thumbnail_url || result.coverUrl || result.cover_url || fallback.thumbnailUrl,
    title: result.title || result.name || fallback.title || fallback.name || 'AI音乐',
    style: result.style || fallback.style || fallback.description || '',
    lyrics: result.lyrics || fallback.lyrics || '',
    prompt: result.prompt || fallback.prompt || '',
    duration: result.durationSeconds || result.duration || fallback.duration,
    createdAt: result.createdAt || fallback.createdAt,
    source,
    sourceLabel: result.sourceLabel || fallback.sourceLabel || sourceLabel,
    metadata,
  };
};

const formatDate = (value) => {
  if (!value) return '';
  const date = typeof value === 'number' ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('zh-CN', { hour12: false });
};

const formatTime = (seconds) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value < 0) return '0:00';
  const mins = Math.floor(value / 60);
  const secs = Math.floor(value % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

const clampPercent = (value) => Math.max(0, Math.min(100, value));

const getCompletionResult = (completion) => completion?.result || completion?.data || completion || {};

const getCompletionCues = (completion) => {
  const result = getCompletionResult(completion);
  if (Array.isArray(result?.cues)) return result.cues;
  if (Array.isArray(completion?.cues)) return completion.cues;
  return [];
};

const getCueDurationForGeneration = (cue) => {
  const duration = Number(cue?.durationSeconds) || Math.max(1, (Number(cue?.endSeconds) || 0) - (Number(cue?.startSeconds) || 0));
  return Math.max(60, Math.min(180, Math.ceil(duration / 30) * 30));
};

const getCueTags = (cue) => {
  if (Array.isArray(cue?.tags)) return cue.tags.map(item => String(item || '').trim()).filter(Boolean).slice(0, 8);
  return String(cue?.styleTags || cue?.style || '短剧BGM，纯音乐')
    .split(/[，,、;；\s]+/)
    .map(item => item.trim())
    .filter(Boolean)
    .slice(0, 8);
};

const getCuePrompt = (cue) => String(cue?.musicPrompt || cue?.prompt || '').trim();

const getScriptCueRuntimeKey = (cue, index) => String(cue?.id || cue?.cueIndex || index);

// ============================
// 工作台语言小组件（本文件内复用）
// ============================

const Panel = ({ title, sub, right, children, className = '' }) => (
  <section
    className={`rounded-lg border ${className}`}
    style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-secondary)' }}
  >
    {(title || right) && (
      <header className="flex items-baseline justify-between gap-3 px-4 pt-3.5 pb-3 border-b" style={{ borderColor: 'var(--color-border-secondary)' }}>
        <div className="flex items-baseline gap-2.5 min-w-0">
          {title && <h3 className="text-[13px] font-semibold" style={{ color: 'var(--color-text-primary)' }}>{title}</h3>}
          {sub && <span className="text-label truncate">{sub}</span>}
        </div>
        {right}
      </header>
    )}
    <div className="p-4">{children}</div>
  </section>
);

const FieldLabel = ({ children }) => (
  <span className="text-label block mb-1.5">{children}</span>
);

const inputStyle = {
  backgroundColor: 'var(--color-bg-primary)',
  border: '1px solid var(--color-border-primary)',
  color: 'var(--color-text-primary)',
};

const SecondaryButton = ({ children, onClick, disabled, className = '', compact = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`${compact ? 'h-7 px-2.5 text-2xs' : 'h-8 px-3 text-xs'} rounded-lg font-medium border cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 inline-flex items-center justify-center gap-1.5 ${className}`}
    style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-tertiary)' }}
  >
    {children}
  </button>
);

const StatusPill = ({ status }) => {
  const map = {
    audio_success: ['已完成', 'var(--color-success)'],
    audio_failed: ['失败', 'var(--color-error)'],
    audio_processing: ['生成中', 'var(--color-warning)'],
    planned: ['待生成', 'var(--color-text-muted)'],
  };
  const [label, color] = map[status] || map.planned;
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium shrink-0"
      style={{ color, backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)` }}
    >
      {label}
    </span>
  );
};

const MusicAgentModern = ({ projectId, episodeId }) => {
  const { getTheme } = useTheme();
  const toast = useToast();
  const theme = getTheme();
  const audioRef = useRef(null);
  const abortRef = useRef(null);
  const generatingCueKeysRef = useRef(new Set());
  const historyAutoplayUrlRef = useRef('');
  const volumeRef = useRef(0.85);

  const [mode, setMode] = useState('single');
  const [prompt, setPrompt] = useState('为这一集短剧生成一首可用于片尾或关键情绪段落的原创音乐。');
  const [lyrics, setLyrics] = useState('');
  const [durationSeconds, setDurationSeconds] = useState(120);
  const [loading, setLoading] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);
  const [historySearch, setHistorySearch] = useState('');
  const [scriptText, setScriptText] = useState('');
  const [scriptUserRequest, setScriptUserRequest] = useState('根据剧情起伏规划纯音乐，必要时切换段落；不要人声、歌词、口播或旁白。');
  const [scriptCues, setScriptCues] = useState([]);
  const [scriptPlanning, setScriptPlanning] = useState(false);
  const [scriptGenerating, setScriptGenerating] = useState(false);
  const [generatingCueKeys, setGeneratingCueKeys] = useState(new Set());

  const hasGeneratingCue = generatingCueKeys.size > 0;
  const scriptBusy = scriptPlanning || scriptGenerating;
  const scriptAnyBusy = scriptBusy || hasGeneratingCue;

  const hasSingleGenerationInput = useMemo(
    () => Boolean(prompt.trim() || lyrics.trim()),
    [lyrics, prompt],
  );

  const canPlanScriptMusic = useMemo(() => {
    return !!projectId && !!scriptText.trim() && !scriptAnyBusy;
  }, [projectId, scriptAnyBusy, scriptText]);

  const filteredHistory = useMemo(() => {
    const keyword = historySearch.trim().toLowerCase();
    if (!keyword) return history;
    return history.filter(item => [
      item.title,
      item.style,
      item.prompt,
      item.lyrics,
      item.sourceLabel,
    ].some(value => String(value || '').toLowerCase().includes(keyword)));
  }, [history, historySearch]);

  const loadHistory = useCallback(async () => {
    if (!projectId) return;
    try {
      const list = await fetchAudioResources(projectId, episodeId);
      const normalized = Array.isArray(list)
        ? list.map(item => normalizeAudio(item, item)).filter(Boolean)
        : [];
      setHistory(normalized);
      if (normalized.length > 0) setSelected(prev => prev || normalized[0]);
    } catch (err) {
      setError(err.message || '音频历史加载失败');
    }
  }, [episodeId, projectId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  useEffect(() => {
    const selectedUrl = selected?.audioUrl || '';
    const shouldAutoplay = !!selectedUrl && historyAutoplayUrlRef.current === selectedUrl;
    historyAutoplayUrlRef.current = '';

    setCurrentTime(0);
    setAudioDuration(0);

    if (!shouldAutoplay) {
      setIsPlaying(false);
      return;
    }

    const audio = audioRef.current;
    if (!audio) {
      setIsPlaying(false);
      return;
    }

    audio.currentTime = 0;
    audio.volume = volumeRef.current;
    audio.play()
      .then(() => setIsPlaying(true))
      .catch(err => {
        setIsPlaying(false);
        if (err?.name === 'AbortError') return;
        setError(err.message || '音频播放失败');
        toast?.error?.(`音频播放失败: ${err.message || '未知错误'}`);
      });
  }, [selected?.audioUrl, toast]);

  const handleSeek = (value) => {
    const next = Number(value);
    setCurrentTime(next);
    if (audioRef.current) {
      audioRef.current.currentTime = next;
    }
  };

  const handleVolume = (value) => {
    const next = Number(value);
    setVolume(next);
    if (audioRef.current) {
      audioRef.current.volume = next;
    }
  };

  const handleGenerate = async () => {
    if (loading) return;
    if (!projectId) {
      const message = '缺少项目信息，请重新进入音乐音频页面';
      setError(message);
      toast?.error?.(message);
      return;
    }
    if (!hasSingleGenerationInput) {
      const message = '请先填写提示词或歌词';
      setError(message);
      toast?.error?.(message);
      return;
    }
    setLoading(true);
    setError('');
    setProgressText('任务已提交');
    abortRef.current = new AbortController();
    try {
      const completion = await generateMusicAudio({
        projectId,
        episodeId,
        prompt: prompt.trim(),
        lyrics: lyrics.trim(),
        durationSeconds,
        language: DEFAULT_MUSIC_LANGUAGE,
        model: DEFAULT_MUSIC_MODEL,
      }, {
        signal: abortRef.current.signal,
        onProgress: (status) => {
          setProgressText(status?.currentStageText || status?.status || '生成中');
        },
      });
      const next = normalizeAudio(completion.result, {
        url: completion.url,
        prompt: prompt.trim(),
        lyrics: lyrics.trim(),
        duration: durationSeconds,
      });
      if (!next) throw new Error('任务完成但没有返回音频地址');
      setSelected(next);
      setHistory(prev => [next, ...prev.filter(item => item.audioUrl !== next.audioUrl)]);
      toast?.success?.('音乐生成完成');
      loadHistory();
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || '音乐生成失败');
        toast?.error?.(`音乐生成失败: ${err.message || '未知错误'}`);
      }
    } finally {
      setLoading(false);
      setProgressText('');
      abortRef.current = null;
    }
  };

  const planScriptCues = async ({ silent = false } = {}) => {
    if (!projectId) throw new Error('缺少 projectId');
    if (!scriptText.trim()) throw new Error('请先输入剧本');
    setScriptPlanning(true);
    setError('');
    setProgressText('正在分析剧本情绪');
    abortRef.current = new AbortController();
    try {
      const provider = normalizeGlobalProvider(localStorage.getItem('ai_provider') || 'wangsugem');
      const completion = await planScriptMusicCues({
        projectId,
        episodeId,
        provider,
        script: scriptText.trim(),
        userRequest: scriptUserRequest.trim(),
        targetDurationSeconds: durationSeconds,
      }, {
        signal: abortRef.current.signal,
        onProgress: (status) => {
          setProgressText(status?.currentStageText || status?.status || '正在分析剧本情绪');
        },
      });
      const cues = getCompletionCues(completion).map((cue, index) => ({
        ...cue,
        cueIndex: Number(cue?.cueIndex) || index + 1,
        status: cue?.status || 'planned',
      }));
      if (!cues.length) throw new Error('AI 未返回可用音乐段落');
      setScriptCues(cues);
      if (!silent) toast?.success?.(`已规划 ${cues.length} 段音乐`);
      return cues;
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || '剧本音乐规划失败');
        toast?.error?.(`剧本音乐规划失败: ${err.message || '未知错误'}`);
      }
      throw err;
    } finally {
      setScriptPlanning(false);
      setProgressText('');
      abortRef.current = null;
    }
  };

  const generateScriptCueAudio = async (cue, index, total) => {
    const titleText = cue?.title || `剧本配乐 ${index + 1}`;
    const promptText = getCuePrompt(cue);
    if (!promptText) throw new Error(`第 ${index + 1} 段缺少音乐提示词`);
    setScriptCues(current => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, status: 'audio_processing', errorMessage: '' } : item
    )));
    const completion = await generateMusicAudio({
      projectId,
      episodeId,
      title: titleText,
      prompt: promptText,
      style: cue?.styleTags || cue?.style || '短剧BGM，纯音乐，情绪递进',
      tags: getCueTags(cue),
      instrumental: true,
      durationSeconds: getCueDurationForGeneration(cue),
      language: DEFAULT_MUSIC_LANGUAGE,
      model: DEFAULT_MUSIC_MODEL,
      source: 'script_music_audio',
      scriptMusicCueIndex: cue?.cueIndex || index + 1,
      scriptMusicSceneSummary: cue?.sceneSummary || cue?.summary || '',
      scriptMusicEmotion: cue?.emotion || '',
      cueStartSeconds: cue?.startSeconds,
      cueEndSeconds: cue?.endSeconds,
    }, {
      onProgress: (status) => setProgressText(status?.currentStageText || status?.status || `正在生成音乐 ${index + 1}/${total}`),
    });
    const result = getCompletionResult(completion);
    const audio = normalizeAudio(result, {
      source: 'script_music_audio',
      title: titleText,
      style: cue?.styleTags || cue?.style,
      prompt: promptText,
      duration: getCueDurationForGeneration(cue),
    });
    if (!audio) throw new Error(`第 ${index + 1} 段任务完成但没有返回音频地址`);
    setHistory(prev => [audio, ...prev.filter(item => item.audioUrl !== audio.audioUrl)]);
    setSelected(audio);
    setScriptCues(current => current.map((item, itemIndex) => (
      itemIndex === index
        ? {
            ...item,
            status: 'audio_success',
            audioUrl: audio.audioUrl,
            thumbnailUrl: audio.thumbnailUrl,
            taskId: result.taskId || result.internalTaskId,
            providerTaskId: result.providerTaskId,
            model: result.model || DEFAULT_MUSIC_MODEL,
            errorMessage: '',
          }
        : item
    )));
    return audio;
  };

  const handleGenerateScriptCue = async (cue, index) => {
    const cueKey = getScriptCueRuntimeKey(cue, index);
    if (!cue || scriptBusy || generatingCueKeysRef.current.has(cueKey)) return;
    generatingCueKeysRef.current.add(cueKey);
    setGeneratingCueKeys(prev => {
      const next = new Set(prev);
      next.add(cueKey);
      return next;
    });
    setError('');
    setProgressText(`正在生成音乐 ${index + 1}/1`);
    try {
      await generateScriptCueAudio(cue, index, 1);
      toast?.success?.('当前段音乐生成完成');
      loadHistory();
    } catch (err) {
      setScriptCues(current => current.map((item, itemIndex) => (
        itemIndex === index ? { ...item, status: 'audio_failed', errorMessage: err.message || '音乐生成失败' } : item
      )));
      setError(err.message || '音乐生成失败');
      toast?.error?.(`音乐生成失败: ${err.message || '未知错误'}`);
    } finally {
      generatingCueKeysRef.current.delete(cueKey);
      setGeneratingCueKeys(prev => {
        const next = new Set(prev);
        next.delete(cueKey);
        return next;
      });
      setProgressText('');
    }
  };

  const handleGenerateScriptMusic = async () => {
    if (scriptAnyBusy) return;
    if (!projectId) {
      const message = '缺少项目信息，请重新进入音乐音频页面';
      setError(message);
      toast?.error?.(message);
      return;
    }
    if (!scriptText.trim() && !scriptCues.length) {
      const message = '请先输入剧本，或先分析出音乐段落';
      setError(message);
      toast?.error?.(message);
      return;
    }
    setScriptGenerating(true);
    setError('');
    try {
      let cues = scriptCues;
      if (!cues.length) {
        setScriptGenerating(false);
        cues = await planScriptCues({ silent: true });
        setScriptGenerating(true);
      }
      let successCount = 0;
      for (let index = 0; index < cues.length; index += 1) {
        setProgressText(`正在生成音乐 ${index + 1}/${cues.length}`);
        try {
          await generateScriptCueAudio(cues[index], index, cues.length);
          successCount += 1;
        } catch (cueError) {
          setScriptCues(current => current.map((item, itemIndex) => (
            itemIndex === index ? { ...item, status: 'audio_failed', errorMessage: cueError.message || '音乐生成失败' } : item
          )));
        }
      }
      await loadHistory();
      if (successCount === cues.length) {
        toast?.success?.(`已生成 ${successCount} 段剧本配乐`);
      } else {
        toast?.warning?.(`已生成 ${successCount}/${cues.length} 段，失败段可单独重试`);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || '剧本配乐生成失败');
        toast?.error?.(`剧本配乐生成失败: ${err.message || '未知错误'}`);
      }
    } finally {
      setScriptGenerating(false);
      setProgressText('');
    }
  };

  const handleScriptCuePromptChange = (index, value) => {
    setScriptCues(current => current.map((item, itemIndex) => (
      itemIndex === index
        ? {
            ...item,
            musicPrompt: value,
            prompt: value,
            errorMessage: item.status === 'audio_failed' ? '' : item.errorMessage,
            status: item.status === 'audio_failed' ? 'planned' : item.status,
          }
        : item
    )));
  };

  const handlePlayToggle = async () => {
    const audio = audioRef.current;
    if (!audio || !selected?.audioUrl) return;
    if (audio.paused) {
      await audio.play();
      setIsPlaying(true);
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleHistoryPlay = async (event, item) => {
    event.stopPropagation();
    if (!item?.audioUrl) return;
    const audio = audioRef.current;
    if (!audio) return;
    const active = selected?.audioUrl === item.audioUrl;
    if (active) {
      try {
        await handlePlayToggle();
      } catch (err) {
        setError(err.message || '音频播放失败');
        toast?.error?.(`音频播放失败: ${err.message || '未知错误'}`);
      }
      return;
    }
    historyAutoplayUrlRef.current = item.audioUrl;
    setError('');
    setSelected(item);
  };

  const selectedDuration = Number(selected?.duration) || 0;
  const displayDuration = audioDuration || selectedDuration || durationSeconds || 1;
  const progressPercent = clampPercent(((currentTime || 0) / displayDuration) * 100);
  const volumePercent = clampPercent(volume * 100);
  const selectedTitle = selected?.title || 'AI音乐';
  const selectedDescription = selected?.style || selected?.prompt || prompt || '等待生成音频';
  const modelLabel = DEFAULT_MUSIC_MODEL.replace('suno-', 'Suno ');

  return (
    <div className="relative h-full w-full overflow-hidden" style={{ background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}>
      <style>{`
        @keyframes music-disc-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .music-range {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          border-radius: 999px;
          background: linear-gradient(90deg, var(--music-accent) 0%, var(--music-accent) var(--music-progress, 0%), var(--color-bg-tertiary) var(--music-progress, 0%), var(--color-bg-tertiary) 100%);
          outline: none;
          cursor: pointer;
        }
        .music-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 999px;
          background: var(--color-text-primary);
          border: 3px solid var(--music-accent);
        }
        .music-range::-moz-range-thumb {
          width: 12px;
          height: 12px;
          border-radius: 999px;
          background: var(--color-text-primary);
          border: 3px solid var(--music-accent);
        }
      `}</style>

      <div className="grid h-full grid-cols-[400px_minmax(0,1fr)] pb-16 max-[1100px]:grid-cols-[340px_minmax(0,1fr)]">
        {/* 左侧：创作控制栏 */}
        <aside className="flex min-h-0 flex-col border-r" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)' }}>
          <div className="flex h-14 shrink-0 items-center border-b px-5" style={{ borderColor: 'var(--color-border-primary)' }}>
            <div className="min-w-0">
              <div className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>音乐音频生成</div>
              <div className="text-label mt-0.5">项目级 BGM、片尾曲、情绪音乐</div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
            {/* 模式切换：分段控件（不撑满） */}
            <div className="inline-flex items-center gap-1 rounded-lg p-1 border" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border-primary)' }}>
              {[
                { id: 'single', label: '单首生成' },
                { id: 'script', label: '剧本配乐' },
              ].map(item => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setMode(item.id)}
                  className="h-7 px-3.5 rounded-md text-xs font-medium transition-colors duration-150 cursor-pointer"
                  style={mode === item.id
                    ? { backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)' }
                    : { color: 'var(--color-text-muted)' }}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {mode === 'script' ? (
              <Panel title="剧本配乐" sub="按剧情起伏规划 1-4 段纯音乐">
                <div className="space-y-3">
                  <label className="block">
                    <FieldLabel>剧本正文</FieldLabel>
                    <textarea
                      value={scriptText}
                      onChange={e => setScriptText(e.target.value)}
                      rows={9}
                      placeholder="粘贴当前集剧本、剧情大纲或完整片段..."
                      className="w-full resize-none rounded-lg px-3 py-2.5 text-xs leading-5 outline-none"
                      style={inputStyle}
                    />
                  </label>

                  <label className="block">
                    <FieldLabel>配乐要求</FieldLabel>
                    <textarea
                      value={scriptUserRequest}
                      onChange={e => setScriptUserRequest(e.target.value)}
                      rows={2}
                      className="w-full resize-none rounded-lg px-3 py-2 text-xs leading-5 outline-none"
                      style={inputStyle}
                    />
                  </label>

                  <div className="flex items-center gap-3">
                    <span className="text-label shrink-0">总时长</span>
                    <AppSelect value={durationSeconds} onChange={e => setDurationSeconds(Number(e.target.value))}
                      className="h-8 w-28 rounded-lg px-2 text-xs outline-none"
                      style={inputStyle}>
                      <option value={60}>60 秒</option>
                      <option value={90}>90 秒</option>
                      <option value={120}>120 秒</option>
                      <option value={180}>180 秒</option>
                    </AppSelect>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <SecondaryButton
                      disabled={!canPlanScriptMusic}
                      onClick={() => planScriptCues()}
                    >
                      {scriptPlanning ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                      分析段落
                    </SecondaryButton>
                    <button
                      type="button"
                      disabled={!projectId || scriptAnyBusy || (!scriptText.trim() && !scriptCues.length)}
                      onClick={handleGenerateScriptMusic}
                      className="h-8 px-3.5 rounded-lg text-xs font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 inline-flex items-center justify-center gap-1.5 text-[var(--color-accent-contrast)]"
                      style={{ backgroundColor: theme.colors.accentPrimary }}
                    >
                      {scriptAnyBusy ? <Loader2 size={12} className="animate-spin" /> : <Music size={12} />}
                      分析并生成
                    </button>
                  </div>
                </div>
              </Panel>
            ) : (
              <Panel title="生成音乐" sub="描述想要的音乐，需要人声时填写歌词">
                <div className="space-y-3">
                  <label className="block">
                    <FieldLabel>提示词</FieldLabel>
                    <textarea
                      value={prompt}
                      onChange={e => setPrompt(e.target.value)}
                      rows={7}
                      placeholder="描述风格、情绪、节奏、乐器和使用场景..."
                      className="w-full resize-y rounded-lg px-3 py-2.5 text-xs leading-5 outline-none"
                      style={inputStyle}
                    />
                  </label>

                  <label className="block">
                    <FieldLabel>歌词（纯音乐可留空）</FieldLabel>
                    <textarea
                      value={lyrics}
                      onChange={e => setLyrics(e.target.value)}
                      rows={6}
                      className="w-full resize-y rounded-lg px-3 py-2.5 text-xs leading-5 outline-none"
                      style={inputStyle}
                    />
                  </label>

                  <div className="flex items-center gap-3">
                    <span className="text-label shrink-0">时长</span>
                    <AppSelect
                      value={durationSeconds}
                      onChange={e => setDurationSeconds(Number(e.target.value))}
                      className="h-8 w-28 rounded-lg px-2 text-xs outline-none"
                      style={inputStyle}
                    >
                      <option value={60}>60 秒</option>
                      <option value={90}>90 秒</option>
                      <option value={120}>120 秒</option>
                      <option value={180}>180 秒</option>
                    </AppSelect>
                  </div>

                  <div className="flex items-center justify-end pt-1">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={handleGenerate}
                      className="h-8 px-4 rounded-lg text-xs font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 inline-flex items-center justify-center gap-1.5 text-[var(--color-accent-contrast)]"
                      style={{ backgroundColor: theme.colors.accentPrimary }}
                    >
                      {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                      {loading ? (progressText || '生成中') : '生成音乐'}
                    </button>
                  </div>
                </div>
              </Panel>
            )}

            {error && (
              <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-xs animate-in fade-in slide-in-from-top-1 duration-200"
                style={{ border: '1px solid color-mix(in srgb, var(--color-error) 35%, transparent)', color: 'var(--color-error)', backgroundColor: 'var(--color-red-light)' }}>
                <AlertCircle size={14} className="mt-0.5 shrink-0" /> {error}
              </div>
            )}
          </div>
        </aside>

        {/* 右侧：结果区 */}
        <section className="flex min-h-0 flex-col" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
          <div className="flex h-14 shrink-0 items-center justify-between border-b px-6" style={{ borderColor: 'var(--color-border-primary)' }}>
            <div className="flex items-baseline gap-2.5">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {mode === 'script' && scriptCues.length > 0 ? '音乐段落与音频历史' : '音频历史'}
              </h3>
              <span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                {filteredHistory.length}/{history.length}
              </span>
            </div>
            <button
              type="button"
              onClick={loadHistory}
              className="w-8 h-8 rounded-md border inline-flex items-center justify-center cursor-pointer transition-colors duration-150"
              style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-secondary)' }}
              title="刷新"
            >
              <RefreshCw size={13} />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4 space-y-4 custom-scrollbar">
            {/* 剧本配乐模式：音乐段落多列网格 */}
            {mode === 'script' && (
              <Panel
                title="音乐段落"
                right={<span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{scriptCues.length || 0} 段</span>}
              >
                {scriptCues.length === 0 ? (
                  <div className="rounded-lg border border-dashed py-8 flex flex-col items-center justify-center gap-1.5" style={{ borderColor: 'var(--color-border-primary)' }}>
                    <div className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>还没有音乐段落</div>
                    <div className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>先在左侧粘贴剧本并点击"分析段落"。</div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-3">
                    {scriptCues.map((cue, index) => {
                      const cueKey = getScriptCueRuntimeKey(cue, index);
                      const status = String(cue?.status || 'planned');
                      const success = status === 'audio_success';
                      const runtimeGenerating = generatingCueKeys.has(cueKey);
                      const processing = runtimeGenerating || (scriptGenerating && status === 'audio_processing');
                      return (
                        <div
                          key={`${cue?.cueIndex || index}-${cue?.title || ''}`}
                          className="rounded-lg border p-3 flex flex-col"
                          style={{
                            borderColor: success ? 'color-mix(in srgb, var(--color-success) 35%, transparent)' : status === 'audio_failed' ? 'color-mix(in srgb, var(--color-error) 35%, transparent)' : 'var(--color-border-secondary)',
                            backgroundColor: 'var(--color-bg-primary)',
                          }}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex items-center gap-2">
                              <span className="truncate text-[13px] font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                                <span className="font-mono-num" style={{ color: 'var(--color-text-muted)' }}>{cue?.cueIndex || index + 1}.</span> {cue?.title || `剧本配乐 ${index + 1}`}
                              </span>
                              <StatusPill status={processing ? 'audio_processing' : status} />
                            </div>
                            <span className="font-mono-num text-2xs shrink-0" style={{ color: 'var(--color-text-muted)' }}>
                              {formatTime(cue?.startSeconds)}-{formatTime(cue?.endSeconds)}
                            </span>
                          </div>
                          <div className="mt-1 text-2xs" style={{ color: 'var(--color-text-muted)' }}>{cue?.emotion || '情绪递进'}</div>
                          <div className="mt-1.5 line-clamp-2 text-xs leading-5" style={{ color: 'var(--color-text-secondary)' }}>
                            {cue?.sceneSummary || cue?.summary || '根据剧情情绪变化生成'}
                          </div>
                          <label className="mt-2 block">
                            <FieldLabel>音乐提示词</FieldLabel>
                            <textarea
                              value={getCuePrompt(cue)}
                              onChange={event => handleScriptCuePromptChange(index, event.target.value)}
                              disabled={scriptBusy || runtimeGenerating}
                              rows={3}
                              className="w-full resize-y rounded-lg px-2.5 py-1.5 text-xs leading-5 outline-none disabled:cursor-not-allowed disabled:opacity-60"
                              style={inputStyle}
                            />
                          </label>
                          {cue?.errorMessage && (
                            <div className="mt-1.5 text-2xs" style={{ color: 'var(--color-error)' }}>{cue.errorMessage}</div>
                          )}
                          <div className="mt-2 flex items-center justify-end gap-2">
                            {cue?.audioUrl && (
                              <button type="button" onClick={() => setSelected(normalizeAudio({ ...cue, source: 'script_music_audio' }, { source: 'script_music_audio', title: cue.title, url: cue.audioUrl }))}
                                className="text-2xs font-medium cursor-pointer mr-auto" style={{ color: theme.colors.accentPrimary }}>
                                播放此音频
                              </button>
                            )}
                            <SecondaryButton
                              disabled={scriptBusy || runtimeGenerating || !getCuePrompt(cue)}
                              onClick={() => handleGenerateScriptCue(cue, index)}
                              compact
                              className="shrink-0"
                            >
                              {processing ? '生成中' : success ? '重生成' : '生成'}
                            </SecondaryButton>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Panel>
            )}

            {/* 音频历史 */}
            <Panel
              title="音频历史"
              right={(
                <label className="relative block w-56">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }} />
                  <input
                    value={historySearch}
                    onChange={e => setHistorySearch(e.target.value)}
                    placeholder="搜索音乐"
                    className="h-7 w-full rounded-md border pl-8 pr-2 text-2xs outline-none"
                    style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
                  />
                </label>
              )}
            >
              {filteredHistory.length === 0 ? (
                <div className="rounded-lg border border-dashed py-10 flex flex-col items-center justify-center gap-1.5" style={{ borderColor: 'var(--color-border-primary)' }}>
                  <Music size={20} strokeWidth={1.5} style={{ color: 'var(--color-text-muted)' }} />
                  <div className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>还没有音乐</div>
                  <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>左侧输入描述后点击生成音乐。</div>
                </div>
              ) : (
                <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--color-border-secondary)', backgroundColor: 'var(--color-bg-primary)' }}>
                  {filteredHistory.map((item, idx) => {
                    const active = selected?.audioUrl === item.audioUrl;
                    const duration = Number(item.duration) || durationSeconds;
                    return (
                      <div
                        key={item.id}
                        onClick={() => setSelected(item)}
                        className="group flex w-full cursor-pointer items-center gap-3 px-3.5 py-2.5 text-left transition-colors duration-150"
                        style={{
                          borderTop: idx > 0 ? '1px solid var(--color-border-secondary)' : 'none',
                          backgroundColor: active ? 'color-mix(in srgb, var(--color-accent-primary) 8%, transparent)' : 'transparent',
                          boxShadow: active ? 'inset 2px 0 0 var(--color-accent-primary)' : 'none',
                        }}
                      >
                        <button
                          type="button"
                          onClick={event => handleHistoryPlay(event, item)}
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md cursor-pointer transition-colors duration-150"
                          style={{
                            backgroundColor: active ? theme.colors.accentPrimary : 'var(--color-bg-tertiary)',
                            color: active ? 'var(--color-accent-contrast)' : 'var(--color-text-secondary)',
                          }}
                          aria-label={active && isPlaying ? '暂停当前音频' : '播放当前音频'}
                        >
                          {active && isPlaying ? <Pause size={12} /> : <Play size={12} />}
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-[13px] font-semibold" style={{ color: 'var(--color-text-primary)' }}>{item.title}</span>
                            <span className="rounded px-1.5 py-0.5 text-2xs shrink-0" style={{ backgroundColor: 'var(--color-accent-light)', color: theme.colors.accentPrimary }}>
                              {modelLabel}
                            </span>
                            {item.sourceLabel && (
                              <span className="rounded px-1.5 py-0.5 text-2xs shrink-0" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>
                                {item.sourceLabel}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 line-clamp-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>{item.style || item.prompt || 'AI音乐'}</div>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          <span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{formatTime(duration)}</span>
                          <span className="font-mono-num text-2xs hidden xl:inline" style={{ color: 'var(--color-text-muted)' }}>{formatDate(item.createdAt) || '-'}</span>
                          <a href={item.audioUrl} download target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                            className="flex h-7 w-7 items-center justify-center rounded-md transition-colors duration-150"
                            style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}
                            title="下载">
                            <Download size={12} />
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>
          </div>
        </section>

        <audio
          ref={audioRef}
          src={selected?.audioUrl || ''}
          preload="metadata"
          onLoadedMetadata={e => {
            setAudioDuration(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0);
            e.currentTarget.volume = volume;
          }}
          onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime || 0)}
          onEnded={() => setIsPlaying(false)}
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
        />

        <footer className="absolute bottom-0 left-0 right-0 z-20 flex h-16 items-center gap-4 border-t px-5"
          style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)' }}>
          <div className="flex min-w-[260px] items-center gap-3">
            <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border"
              style={{
                borderColor: 'var(--color-border-primary)',
                backgroundColor: 'var(--color-bg-tertiary)',
                animation: isPlaying ? 'music-disc-spin 3.8s linear infinite' : 'none',
              }}>
              <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: theme.colors.accentPrimary }} />
            </div>
            <div className="min-w-0">
              <div className="truncate text-[13px] font-semibold">{selected?.audioUrl ? selectedTitle : '未选择音频'}</div>
              <div className="truncate text-2xs" style={{ color: 'var(--color-text-muted)' }}>{selected?.audioUrl ? selectedDescription : '从右侧历史选择，或左侧生成新音乐'}</div>
            </div>
          </div>

          <button
            type="button"
            onClick={handlePlayToggle}
            disabled={!selected?.audioUrl}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[var(--color-accent-contrast)] cursor-pointer disabled:opacity-40 transition-colors duration-150"
            style={{ backgroundColor: theme.colors.accentPrimary }}
            aria-label={isPlaying ? '暂停' : '播放'}
          >
            {isPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
          </button>

          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex justify-between font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(displayDuration)}</span>
            </div>
            <input
              className="music-range w-full"
              type="range"
              aria-label="音频播放进度"
              min="0"
              max={displayDuration}
              step="0.1"
              value={Math.min(currentTime, displayDuration)}
              disabled={!selected?.audioUrl}
              onChange={e => handleSeek(e.target.value)}
              style={{
                '--music-accent': theme.colors.accentPrimary,
                '--music-progress': `${progressPercent}%`,
              }}
            />
          </div>

          <div className="flex w-40 shrink-0 items-center gap-2.5">
            <Volume2 size={14} style={{ color: 'var(--color-text-muted)' }} />
            <input
              className="music-range flex-1"
              type="range"
              aria-label="音量"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={e => handleVolume(e.target.value)}
              style={{ '--music-accent': theme.colors.accentPrimary, '--music-progress': `${volumePercent}%` }}
            />
          </div>

          {selected?.audioUrl && (
            <a href={selected.audioUrl} download target="_blank" rel="noreferrer"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
              style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}
              title="下载">
              <Download size={14} />
            </a>
          )}
        </footer>
      </div>
    </div>
  );
};

export default MusicAgentModern;
